import React from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import { TranslationKeyEnum } from '../../types/constants';
import { AdoptionsInsights, TimePeriod } from '../../types/insights';
import { Card } from '../Card/Card';
import { MonthlyBarChart } from '../charts/MonthlyBarChart';
import { Tooltip } from '../Tooltip';
import { useShelterState } from '../../hooks/useShelter';

interface AdoptionsCardProps {
  data: AdoptionsInsights;
  className?: string;
  selectedPeriod: TimePeriod;
  selectedYear: number | null;
  selectedMonth: string | null;
  selectedQuarter: string | null;
}

export const AdoptionsCard: React.FC<AdoptionsCardProps> = ({
  data,
  className = '',
  selectedPeriod,
  selectedYear = '',
  selectedMonth = '',
  selectedQuarter = '',
}) => {
  const { t } = useTranslation();
  const { shelter } = useShelterState();

  // Prepare chart data for stacked bars
  const chartData = data.monthlyData.map((point) => ({
    month: point.month,
    year: point.year,
    adultDogs: point.adultDogs,
    puppies: point.puppies,
    adultCats: point.adultCats,
    kittens: point.kittens,
    reportMissing: point.reportMissing,
    totalAdoptions: point.total,
    isPastMonth: point.isPastMonth,
  }));

  const chartSeries = [
    {
      key: 'adultDogs',
      color: '#CDDEEE',
      label: t(TranslationKeyEnum.AdultDogs),
    },
    {
      key: 'puppies',
      color: '#6396C9',
      label: t(TranslationKeyEnum.Puppies),
    },
    {
      key: 'adultCats',
      color: '#0054A4',
      label: t(TranslationKeyEnum.AdultCats),
    },
    {
      key: 'kittens',
      color: '#003672',
      label: t(TranslationKeyEnum.Kittens),
    },
  ];

  const translationKeyMap = {
    [TimePeriod.YEAR]: t(TranslationKeyEnum.AdoptionsIn, selectedYear + ''),
    [TimePeriod.QUARTER]: t(TranslationKeyEnum.AdoptionsIn, selectedQuarter ?? ''),
    [TimePeriod.MONTH]: t(TranslationKeyEnum.AdoptionsIn, selectedMonth + ' ' + selectedYear),
  };

  const Legend: React.FC = () => (
    <>
      <div className="flex items-center gap-3">
        <div
          className="w-3.5 h-3.5 rounded-full"
          style={{ backgroundColor: chartSeries[0].color }}
        />
        <span className="text-base font-medium text-black/60">
          {t(TranslationKeyEnum.AdultDogs)}
        </span>
      </div>
      <div className="flex items-center gap-3">
        <div
          className="w-3.5 h-3.5 rounded-full"
          style={{ backgroundColor: chartSeries[1].color }}
        />
        <span className="text-base font-medium text-black/60">{t(TranslationKeyEnum.Puppies)}</span>
      </div>
      <div className="flex items-center gap-3">
        <div
          className="w-3.5 h-3.5 rounded-full"
          style={{ backgroundColor: chartSeries[2].color }}
        />
        <span className="text-base font-medium text-black/60">
          {t(TranslationKeyEnum.AdultCats)}
        </span>
      </div>
      <div className="flex items-center gap-3">
        <div
          className="w-3.5 h-3.5 rounded-full"
          style={{ backgroundColor: chartSeries[3].color }}
        />
        <span className="text-base font-medium text-black/60">{t(TranslationKeyEnum.Kittens)}</span>
      </div>
    </>
  );

  const fallbackDisplay = shelter?.is_petszel_enabled ? '0' : '--';
  const totalDisplay = data.totalAdoptions > 0 || data.hasCompleteData
    ? data.totalAdoptions
    : fallbackDisplay;

  return (
    <Card
      background="white"
      radius="large"
      padding="none"
      className={`border border-black/20 overflow-visible ${className}`}
    >
      <div className="flex flex-col gap-12 p-12">
        {/* Header */}
        <div className="flex justify-between items-center">
          <h3 className="text-3xl font-bold text-black/90 tracking-tight">
            {t(TranslationKeyEnum.TotalAdoptions)}
          </h3>

          {/* Legend */}
          <div className="hidden lg:flex items-center gap-6">
            <Legend />
          </div>
        </div>

        {/* Content */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end gap-18">
          {/* Left Side - Status and Metrics */}
          <div className="flex flex-wrap sm:flex-nowrap gap-8 justify-between flex-shrink-0 w-full lg:w-fit">
            {/* Total Number Section */}
            <div className="flex flex-col gap-3">
              <div className="text-8xl font-bold text-black/90 leading-none">
                {totalDisplay}
              </div>
              <div className="flex items-center gap-1">
                <span className="text-base font-semibold text-black/90">
                  {translationKeyMap[selectedPeriod]}
                </span>
                <Tooltip
                  align="right"
                  content={
                    <div className="w-[360px]  p-4">
                      <div className="text-sm font-extralight text-white leading-[1.375]">
                        {t(TranslationKeyEnum.AdoptionsCardTooltip)}
                      </div>
                    </div>
                  }
                />
              </div>
            </div>
            {/* show below legend on mobile */}
            <div className="flex flex-col lg:hidden items-start gap-2 flex-shrink-0">
              <Legend />
            </div>
          </div>

          {/* Chart */}
          <div className="w-full">
            <MonthlyBarChart
              type={'adoptions'}
              data={chartData}
              series={chartSeries}
              height={314}
              selectedPeriod={selectedPeriod}
              hasAdoptionData={data.hasAdoptionData}
            />
          </div>
        </div>
      </div>
    </Card>
  );
};

export default AdoptionsCard;
